/**
 * Modern Header Module - Fixed & Optimized
 * Handles mobile menu, mega menus, cart drawer, and sticky navigation
 * 
 * Fixed: Blur effect sticking after closing cart drawer by clicking outside
 * - Added comprehensive blur cleanup in #closeCartDrawer()
 * - Added failsafe blur clearing in #hideAllMegaMenus()
 * - Added document-level click handler as final failsafe
 */

class ModernHeader {
  constructor() {
    // Core elements
    this.mobileToggle = document.getElementById('mobile-menu-toggle');
    this.navBar = document.getElementById('main-navigation');
    this.header = document.querySelector('.modern-header');
    
    // State management
    this.scrollPosition = 0;
    this.isDesktop = window.innerWidth >= 1024;
    this.megaMenuTimeout = null;
    this.headerHeight = this.header?.offsetHeight || 0;
    
    // Initialize all functionality
    this.#init();
    
    // Ensure variables are set after DOM is fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.#updateHeaderVariables();
      });
    }
  }
  
  /**
   * Initialize all header functionality
   * @private
   */
  #init() {
    this.#updateHeaderVariables();
    this.#handleMobileMenu();
    this.#handleMegaMenus();
    this.#handleCartUpdates();
    this.#handleCartDrawer();
    this.#handleStickyNavigation();
    this.#handleResponsiveChanges();
    this.#initMegaMenuImageHover();
    this.#handleSimpleScrollToTop();
  }
  
  /**
   * Update CSS custom properties for header dimensions
   * @private
   */
  #updateHeaderVariables() {
    if (!this.header) return;
    
    const headerHeight = this.header.offsetHeight || 100; // Fallback to 100px
    const headerOffset = this.header.classList.contains('modern-header--sticky') ? headerHeight : 0;
    
    // Set CSS custom properties on the document root
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    document.documentElement.style.setProperty('--header-offset', `${headerOffset}px`);
    
    // Also update the instance property
    this.headerHeight = headerHeight;
  }
  
  /**
   * Handle mobile menu toggle functionality
   * @private
   */
  #handleMobileMenu() {
    this.mobileToggle?.addEventListener('click', () => {
      this.#toggleMobileMenu();
    });
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.navBar?.classList.contains('open')) {
        this.#closeMobileMenu();
      }
    });
  }
  
  /**
   * Toggle mobile menu state
   * @private
   */
  #toggleMobileMenu() {
    const isOpen = this.navBar?.classList.toggle('open');
    
    if (isOpen) {
      this.#lockBodyScroll();
    } else {
      this.#unlockBodyScroll();
    }
    
    this.#updateMobileToggleIcon(isOpen);
  }
  
  /**
   * Close mobile menu
   * @private
   */
  #closeMobileMenu() {
    this.navBar?.classList.remove('open');
    this.#unlockBodyScroll();
    this.#updateMobileToggleIcon(false);
  }
  
  /**
   * Update mobile toggle icon state
   * @private
   * @param {boolean} isOpen - Whether menu is open
   */
  #updateMobileToggleIcon(isOpen) {
    const barsIcon = this.mobileToggle?.querySelector('.mobile-menu-bars');
    const closeIcon = this.mobileToggle?.querySelector('.mobile-menu-close');
    
    if (barsIcon && closeIcon) {
      barsIcon.style.display = isOpen ? 'none' : 'flex';
      closeIcon.style.display = isOpen ? 'block' : 'none';
    }
  }
  
  /**
   * Handle mega menu interactions
   * @private
   */
  #handleMegaMenus() {
    const menuItems = document.querySelectorAll('.modern-header__nav-item');

    menuItems.forEach(item => {
      const link = item.querySelector('.modern-header__nav-link');
      const megaMenu = item.querySelector('.modern-header__mega-menu');

      if (!megaMenu) return;

      // Desktop hover behavior
      if (this.isDesktop) {
        this.#setupDesktopMegaMenu(item, megaMenu);
      }

      // Mobile click behavior
      this.#setupMobileMegaMenu(link, megaMenu);
    });

    // Close mega menus on document click and ensure blur cleanup
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.modern-header__nav-item')) {
        this.#hideAllMegaMenus();
      }
      
      // Additional failsafe: if clicking outside of any header element,
      // cart drawer, or mega menu, clear all blur effects
      if (!e.target.closest('.modern-header') && 
          !e.target.closest('#Cart-Drawer') && 
          !e.target.closest('.modern-header__mega-menu')) {
        // Small delay to allow other click handlers to process first
        setTimeout(() => {
          const cartDrawer = document.getElementById('Cart-Drawer');
          const hasOpenMegaMenu = document.querySelector('.modern-header__mega-menu.show');
          
          // Only clear blur if no overlays should be open
          if (!cartDrawer?.classList.contains('active') && !hasOpenMegaMenu) {
            this.#clearAllBlurEffects();
          }
        }, 50);
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#hideAllMegaMenus();
      }
    });
  }
  
  /**
   * Setup desktop mega menu hover behavior
   * @private
   * @param {Element} item - Navigation item
   * @param {Element} megaMenu - Mega menu element
   */
  #setupDesktopMegaMenu(item, megaMenu) {
    let enterTimeout;
    let leaveTimeout;

    // Ensure mega menu has correct positioning
    megaMenu.style.position = 'fixed';
    megaMenu.style.top = '100px';
    megaMenu.style.left = '0';
    megaMenu.style.right = '0';
    megaMenu.style.width = '100%';
    megaMenu.style.zIndex = '998';

    item.addEventListener('mouseenter', () => {
      if (!this.isDesktop) return;
      
      clearTimeout(leaveTimeout);
      clearTimeout(enterTimeout);
      
      // Small delay to prevent accidental triggers
      enterTimeout = setTimeout(() => {
        const currentlyVisible = document.querySelector('.modern-header__mega-menu.show');
        
        if (currentlyVisible && currentlyVisible !== megaMenu) {
          currentlyVisible.classList.remove('show');
        }
        
        // Ensure position is set before showing
        megaMenu.style.top = '100px';
        megaMenu.classList.add('show');
      }, 100);
    });

    item.addEventListener('mouseleave', () => {
      if (!this.isDesktop) return;
      
      clearTimeout(enterTimeout);
      clearTimeout(leaveTimeout);
      
      leaveTimeout = setTimeout(() => {
        megaMenu.classList.remove('show');
      }, 300);
    });
  }
  
  /**
   * Setup mobile mega menu click behavior
   * @private
   * @param {Element} link - Navigation link
   * @param {Element} megaMenu - Mega menu element
   */
  #setupMobileMegaMenu(link, megaMenu) {
    link?.addEventListener('click', (e) => {
      if (!this.isDesktop && megaMenu) {
        e.preventDefault();
        e.stopPropagation();
        this.#toggleMegaMenu(megaMenu);
      }
    });
  }
  
  /**
   * Toggle mega menu visibility
   * @private
   * @param {Element} megaMenu - Mega menu element
   */
  #toggleMegaMenu(megaMenu) {
    const isVisible = megaMenu.classList.contains('show');

    // Close all other mega menus
    document.querySelectorAll('.modern-header__mega-menu').forEach(menu => {
      if (menu !== megaMenu) {
        menu.classList.remove('show');
      }
    });

    // Toggle current mega menu
    megaMenu.classList.toggle('show', !isVisible);
    
    // Toggle blur effect
    if (!isVisible) {
      document.body.classList.add('mega-menu-open');
    } else {
      document.body.classList.remove('mega-menu-open');
    }
  }
  
  /**
   * Hide all mega menus
   * @private
   */
  #hideAllMegaMenus() {
    document.querySelectorAll('.modern-header__mega-menu.show').forEach(menu => {
      menu.classList.remove('show');
    });
    
    // Remove all blur effects to ensure clean state
    document.body.classList.remove('mega-menu-open');
    
    // Only remove cart-drawer-open if no cart drawer is actually active
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (!cartDrawer?.classList.contains('active')) {
      document.body.classList.remove('cart-drawer-open');
    }
  }
  
  /**
   * Lock body scroll (for mobile menu and cart drawer)
   * @private
   */
  #lockBodyScroll() {
    if (document.body.hasAttribute('data-scroll-locked')) return;
    
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Apply to fixed elements
      if (this.header && window.getComputedStyle(this.header).position === 'fixed') {
        this.header.style.paddingRight = `${scrollbarWidth}px`;
      }
    }
    
    document.body.style.overflow = 'hidden';
    document.body.setAttribute('data-scroll-locked', 'true');
  }
  
  /**
   * Unlock body scroll
   * @private
   */
  #unlockBodyScroll() {
    if (!document.body.hasAttribute('data-scroll-locked')) return;
    
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    
    if (this.header) {
      this.header.style.paddingRight = '';
    }
    
    document.body.removeAttribute('data-scroll-locked');
  }
  
  /**
   * Handle cart count updates
   * @private
   */
  #handleCartUpdates() {
    document.addEventListener('cart:updated', (event) => {
      this.#updateCartCount(event.detail?.item_count || 0);
    });
  }
  
  /**
   * Update cart count display
   * @private
   * @param {number} count - Cart item count
   */
  #updateCartCount(count) {
    const cartContainer = document.querySelector('.modern-header__cart');
    let countElement = cartContainer?.querySelector('.modern-header__cart-count');

    if (count > 0) {
      if (!countElement) {
        countElement = document.createElement('span');
        countElement.className = 'modern-header__cart-count';
        cartContainer?.appendChild(countElement);
      }
      countElement.textContent = count;
    } else {
      countElement?.remove();
    }
  }

  /**
   * Handle cart drawer functionality
   * @private
   */
  #handleCartDrawer() {
    const cartButton = document.getElementById('cart-drawer-toggle');
    const cartDrawer = document.getElementById('Cart-Drawer');
    const closeButton = cartDrawer?.querySelector('.side-panel-close');

    if (!cartButton || !cartDrawer) return;

    // Open cart drawer
    cartButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.#openCartDrawer(cartDrawer);
    });

    // Close cart drawer
    closeButton?.addEventListener('click', () => {
      this.#closeCartDrawer(cartDrawer);
    });

    // Close on outside click
    cartDrawer.addEventListener('click', (e) => {
      if (e.target === cartDrawer) {
        this.#closeCartDrawer(cartDrawer);
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer.classList.contains('active')) {
        this.#closeCartDrawer(cartDrawer);
      }
    });
  }
  
  /**
   * Open cart drawer
   * @private
   * @param {Element} cartDrawer - Cart drawer element
   */
  #openCartDrawer(cartDrawer) {
    cartDrawer.classList.add('active');
    this.#lockBodyScroll();
    document.body.classList.add('cart-drawer-open');
    cartDrawer.focus();
  }
  
  /**
   * Close cart drawer
   * @private
   * @param {Element} cartDrawer - Cart drawer element
   */
  #closeCartDrawer(cartDrawer) {
    cartDrawer.classList.remove('active');
    this.#unlockBodyScroll();
    
    // Remove all blur-causing classes to ensure clean state
    document.body.classList.remove('cart-drawer-open');
    document.body.classList.remove('mega-menu-open');
    
    // Also hide any open mega menus that might be causing blur
    this.#hideAllMegaMenus();
  }
  
  /**
   * Handle sticky header behavior
   * @private
   */
  #handleStickyNavigation() {
    if (!this.header) return;
    
    let lastScrollY = 0;
    let ticking = false;
    
    const updateScrollState = () => {
      const currentScrollY = window.scrollY;
      
      // Add shadow class when scrolled
      if (currentScrollY > 10) {
        this.header.classList.add('scrolled');
      } else {
        this.header.classList.remove('scrolled');
      }
      
      lastScrollY = currentScrollY;
      ticking = false;
    };
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollState);
        ticking = true;
      }
    }, { passive: true });
  }
  
  /**
   * Handle responsive behavior changes
   * @private
   */
  #handleResponsiveChanges() {
    let resizeTimeout;
    
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      
      resizeTimeout = setTimeout(() => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth >= 1024;
        
        // If switching from mobile to desktop
        if (!wasDesktop && this.isDesktop) {
          this.#closeMobileMenu();
          this.#hideAllMegaMenus();
          this.#reinitMegaMenus();
        }
        
        // If switching from desktop to mobile
        if (wasDesktop && !this.isDesktop) {
          this.#hideAllMegaMenus();
        }
        
        // Update header height and CSS variables
        this.#updateHeaderVariables();
      }, 250);
    });
  }
  
  /**
   * Reinitialize mega menus after responsive change
   * @private
   */
  #reinitMegaMenus() {
    const menuItems = document.querySelectorAll('.modern-header__nav-item');
    
    menuItems.forEach(item => {
      const megaMenu = item.querySelector('.modern-header__mega-menu');
      if (megaMenu && this.isDesktop) {
        // Remove any inline styles that might have been added
        megaMenu.style.removeProperty('top');
        megaMenu.style.removeProperty('max-height');
        megaMenu.style.removeProperty('margin-right');
      }
    });
  }
  
  /**
   * Initialize hover image change functionality for mega menu
   * @private
   */
  #initMegaMenuImageHover() {
    const megaMenus = document.querySelectorAll('.modern-header__mega-menu');
    
    megaMenus.forEach(megaMenu => {
      const linkItems = megaMenu.querySelectorAll('.modern-header__mega-link-item[data-hover-image]');
      const imageElement = megaMenu.querySelector('.modern-header__mega-image');
      const placeholder = megaMenu.querySelector('.modern-header__mega-image-placeholder');
      
      if (!imageElement) return;
      
      const defaultSrc = imageElement.src;
      const isImageHidden = imageElement.style.display === 'none';
      
      linkItems.forEach(linkItem => {
        const hoverImage = linkItem.getAttribute('data-hover-image');
        
        if (hoverImage) {
          // Preload hover image
          const img = new Image();
          img.src = hoverImage;
          
          linkItem.addEventListener('mouseenter', () => {
            imageElement.src = hoverImage;
            
            if (isImageHidden || imageElement.style.display === 'none') {
              imageElement.style.display = 'block';
              imageElement.style.opacity = '1';
              if (placeholder) {
                placeholder.style.display = 'none';
              }
            }
          });
          
          linkItem.addEventListener('mouseleave', () => {
            imageElement.src = defaultSrc;
            
            if (isImageHidden) {
              imageElement.style.display = 'none';
              if (placeholder) {
                placeholder.style.display = 'flex';
              }
            }
          });
        }
      });
    });
  }

  /**
   * Handle simple scroll to top
   * @private
   */
  #handleSimpleScrollToTop() {
    const logo = document.querySelector('.modern-header__logo');
    
    if (logo) {
      let clickCount = 0;
      let clickTimeout;
      
      logo.addEventListener('click', (e) => {
        clickCount++;
        
        if (clickCount === 1) {
          clickTimeout = setTimeout(() => {
            clickCount = 0;
          }, 400);
        } else if (clickCount === 2) {
          clearTimeout(clickTimeout);
          clickCount = 0;
          
          if (window.scrollY > 100) {
            e.preventDefault();
            this.#smoothScrollToTop();
          }
        }
      });
    }
  }

  /**
   * Smooth scroll to top of page
   * @private
   */
  #smoothScrollToTop() {
    const startPosition = window.scrollY;
    const startTime = performance.now();
    const duration = 500;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      
      const currentPosition = startPosition * (1 - easedProgress);
      window.scrollTo(0, currentPosition);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  /**
   * Public method to refresh header state
   */
  refresh() {
    this.#hideAllMegaMenus();
    this.#closeMobileMenu();
  }
  
  /**
   * Public method to update cart count
   * @param {number} count - New cart count
   */
  updateCartCount(count) {
    this.#updateCartCount(count);
  }

  /**
   * Public method to enable/disable hide on scroll
   * @param {boolean} enable - Whether to enable hide on scroll
   */
  setHideOnScroll(enable) {
    this.stickyOptions.hideOnScroll = enable;
    if (!enable) {
      // Reset header position if disabling
      this.header.style.transform = 'translateY(0)';
      this.header.classList.remove('hide-on-scroll');
    }
  }
  
  /**
   * Public method to update sticky options
   * @param {Object} options - Options to update
   */
  updateStickyOptions(options) {
    this.stickyOptions = { ...this.stickyOptions, ...options };
  }

  /**
   * Public method to close all overlays
   */
  closeAllOverlays() {
    this.#hideAllMegaMenus();
    this.#closeMobileMenu();
    
    const cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer?.classList.contains('active')) {
      this.#closeCartDrawer(cartDrawer);
    }
    
    // Force clear all blur effects as failsafe
    this.#clearAllBlurEffects();
  }
  
  /**
   * Clear all blur effects from the page
   * @private
   */
  #clearAllBlurEffects() {
    // Remove all possible blur-causing classes
    document.body.classList.remove('mega-menu-open', 'cart-drawer-open');
    
    // Ensure body scroll is unlocked
    if (document.body.hasAttribute('data-scroll-locked')) {
      this.#unlockBodyScroll();
    }
  }
  
  /**
   * Public method to clear all blur effects (useful for debugging)
   */
  clearAllBlurEffects() {
    this.#clearAllBlurEffects();
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.modernHeader = new ModernHeader();
  });
} else {
  // DOM already loaded
  window.modernHeader = new ModernHeader();
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ModernHeader;
}